import { defaultGradingScale, DraftQuestion, GradeRange, newQuestionId, OPTION_LETTERS } from "./quizModel";

export interface ParsedQuiz {
    title: string;
    description: string;
    level: string;
    estimatedMinutes: number | null;
    questions: DraftQuestion[];
    gradingScale: GradeRange[];
    gradingFromText: boolean;
    keyCount: number;
    warnings: string[];
}

// "[17:11, 19/10/2026] +52 1 55 8616 2467: " al inicio de cada mensaje copiado de WhatsApp.
const WHATSAPP_PREFIX = /^\[\d{1,2}:\d{2}(?::\d{2})?(?:\s?[ap]\.?\s?m\.?)?,\s*\d{1,2}\/\d{1,2}\/\d{2,4}\]\s*[^:]+:\s?/i;
// Exige espacio y texto despues del numero para no confundir "20–25 minutos" con una pregunta.
const QUESTION_LINE = /^(\d{1,3})\s*[.)\-–]\s+(?=\D)(.+)$/;
const OPTION_LINE = /^([A-Fa-f])\s*[).:\-–]\s*(.+)$/;
const GRADE_LINE = /^(\d{1,3})\s*[–—-]\s*(\d{1,3})\s*(?:correctas?|aciertos?|respuestas?)\s*[\t:|\-–—]*\s*(.+)$/i;
const KEY_HEADER = /respuestas?\s+correctas|clave\s+de\s+respuestas|hoja\s+de\s+respuestas/i;
const KEY_PAIR = /(\d{1,3})\s*[.)\-–:=]?\s*([A-F])\b/g;
const TAB_KEY_LINE = /^(\d{1,3})\t+([A-F])$/;
const CORRECT_MARK = /\s*(✅|✔️?|☑️?|\(correcta\)|\[correcta\])\s*/i;

export const parseQuizText = (raw: string): ParsedQuiz => {
    const warnings: string[] = [];
    const header: string[] = [];
    const gradingScale: GradeRange[] = [];
    const key = new Map<number, string>();
    const questions: { number: number; text: string; options: string[]; marked: number }[] = [];
    let current: (typeof questions)[number] | null = null;
    let inKey = false;
    let ignored = 0;

    raw.replace(/\r/g, "").replace(/[‎‏‪-‮]/g, "").split("\n").forEach((rawLine) => {
        const isNewMessage = WHATSAPP_PREFIX.test(rawLine.trim());
        const line = rawLine.trim().replace(WHATSAPP_PREFIX, "").replace(/\*/g, "").trim();
        if (isNewMessage) {
            // Cada mensaje nuevo cierra la pregunta en curso para no pegarle texto del chat.
            current = null;
            inKey = false;
        }
        if (!line) return;

        if (KEY_HEADER.test(line)) {
            inKey = true;
            current = null;
            return;
        }
        const grade = line.match(GRADE_LINE);
        if (grade) {
            const [a, b] = [Number(grade[1]), Number(grade[2])];
            gradingScale.push({ min: Math.min(a, b), max: Math.max(a, b), label: grade[3].trim() });
            return;
        }
        if (inKey || TAB_KEY_LINE.test(line)) {
            const pairs = Array.from(line.matchAll(KEY_PAIR));
            pairs.forEach((m) => key.set(Number(m[1]), m[2]));
            return;
        }

        const option = line.match(OPTION_LINE);
        if (option && current) {
            const marked = CORRECT_MARK.test(option[2]);
            if (marked) current.marked = current.options.length;
            current.options.push(option[2].replace(CORRECT_MARK, " ").trim());
            return;
        }
        const question = line.match(QUESTION_LINE);
        if (question) {
            current = { number: Number(question[1]), text: question[2].trim(), options: [], marked: -1 };
            questions.push(current);
            return;
        }
        if (current && !current.options.length) {
            current.text = `${current.text} ${line}`;
            return;
        }
        if (!questions.length) {
            header.push(line);
            return;
        }
        ignored += 1;
    });

    // Encabezado: "GOOD LOOK®", "QUIZ DE ...", "20 preguntas", "20–25 minutos", "Nivel ...".
    let expected: number | null = null;
    let estimatedMinutes: number | null = null;
    let level = "";
    const textLines: string[] = [];
    header.forEach((line) => {
        const count = line.match(/^(\d+)\s*preguntas?$/i);
        const minutes = line.match(/^(\d+)\s*(?:[–—-]\s*(\d+))?\s*min(?:utos)?\.?$/i);
        const lvl = line.match(/^nivel\s*:?\s*(.+)$/i);
        if (count) expected = Number(count[1]);
        else if (minutes) estimatedMinutes = Number(minutes[2] ?? minutes[1]);
        else if (lvl) level = lvl[1].trim();
        else textLines.push(line);
    });
    const titleIndex = Math.max(0, textLines.findIndex((l) => /quiz|cuestionario|evaluaci[oó]n/i.test(l)));
    const title = textLines[titleIndex] ?? "";
    const description = textLines.filter((_, i) => i !== titleIndex).join(" · ");

    const draft: DraftQuestion[] = questions.map((q) => {
        const keyLetter = key.get(q.number);
        let correctIndex = keyLetter ? OPTION_LETTERS.indexOf(keyLetter) : q.marked;
        if (correctIndex >= q.options.length) {
            warnings.push(`Pregunta ${q.number}: la respuesta ${keyLetter} no existe entre sus opciones.`);
            correctIndex = -1;
        } else if (correctIndex < 0) {
            warnings.push(`Pregunta ${q.number}: no se encontró la respuesta correcta.`);
        }
        if (q.options.length < 2) warnings.push(`Pregunta ${q.number}: solo se detectaron ${q.options.length} opciones.`);
        return { id: newQuestionId(), text: q.text, options: q.options, correctIndex };
    });

    questions.forEach((q, i) => {
        if (q.number !== i + 1) warnings.push(`Numeración salteada: se esperaba la pregunta ${i + 1} y llegó la ${q.number}.`);
    });
    if (expected && expected !== questions.length) warnings.push(`El encabezado indica ${expected} preguntas y se detectaron ${questions.length}.`);
    if (key.size && key.size !== questions.length) warnings.push(`La clave tiene ${key.size} respuestas para ${questions.length} preguntas.`);
    if (ignored) warnings.push(`${ignored} línea(s) fuera de una pregunta se ignoraron (mensajes del chat u opciones en varias líneas).`);

    return {
        title,
        description,
        level,
        estimatedMinutes,
        questions: draft,
        gradingScale: gradingScale.length ? gradingScale.sort((a, b) => b.min - a.min) : defaultGradingScale(draft.length),
        gradingFromText: gradingScale.length > 0,
        keyCount: key.size,
        warnings
    };
};
